import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Card, CardContent } from '../components/ui/Card';
import { AlertTriangle, Clock, Mail, CheckCircle2 } from 'lucide-react';
import { CFA_INFO } from '../data/constants';
import { useSeo } from '../hooks/useSeo';
export function Reclamations() {
  useSeo({
    title: 'Réclamations et médiation',
    description: 'Comment adresser une réclamation à Zillenium Alternance : procédure, délais de traitement, contacts et recours possibles (médiateur de l’apprentissage). Une démarche qualité transparente.',
    path: '/reclamations'
  });
  return <div className="flex flex-col w-full" data-id="element-2460" data-mp="2xhrjkhx2gmil">
      <PageHeader title="Gestion des Réclamations" subtitle="Votre satisfaction est notre priorité. Nous sommes à votre écoute pour traiter toute insatisfaction." data-id="element-2461" data-mp="2xgpwff2s09ki" />

      <div className="py-16 md:py-24 bg-white relative overflow-hidden" data-id="element-2462" data-mp="1m1axw6ux59na">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-2463" data-mp="27dcnzk4yjpvc" />
        <div className="container max-w-5xl mx-auto relative z-10" data-id="element-2464" data-mp="2cbnvvpbozqax">
          <section className="text-center mb-20" data-id="element-2465" data-mp="1xfoqimfyw49h">
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-3xl mx-auto font-medium" data-id="element-2466" data-mp="1xgzrkei12xww">
              Dans le cadre de notre démarche d'amélioration continue, Zillenium
              Alternance met à disposition de l'ensemble de ses parties
              prenantes (apprentis, entreprises, financeurs, partenaires) une
              procédure de gestion des réclamations claire et transparente.
            </p>
          </section>

          <div className="grid md:grid-cols-2 gap-10 mb-20" data-id="element-2467" data-mp="1jojwbzvdc46j">
            <Card className="border-none shadow-xl bg-white hover:-translate-y-2 transition-transform duration-300 group" data-id="element-2468" data-mp="15npkt8d0xn3n">
              <div className="h-2 w-full bg-primary" data-id="element-2469" data-mp="19pklqbr1lygw" />
              <CardContent className="p-8 md:p-10" data-id="element-2470" data-mp="19rfc45l32isd">
                <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-8 flex items-center gap-4" data-id="element-2471" data-mp="3cd1dvwsbd7uf">
                  <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2472" data-mp="37jor7s773b4b">
                    <AlertTriangle className="w-6 h-6 text-primary animate-soft-pulse" data-id="element-2473" data-mp="2xn3nrbatq82l" />
                  </div>
                  Motifs de réclamation possibles
                </h3>
                <ul className="space-y-5" data-id="element-2474" data-mp="3v8hqnyn1013o">
                  <li className="flex items-start gap-4" data-id="element-2475" data-mp="3qbhldpy032dm">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2.5 shrink-0" data-id="element-2476" data-mp="3q9y4bx4isnas"></div>
                    <span className="text-muted-foreground leading-relaxed" data-id="element-2477" data-mp="3leiqv6afctew">
                      <strong className="text-primary-dark font-bold" data-id="element-2478" data-mp="3hat50ol4wpxk">
                        Pédagogique :
                      </strong>{' '}
                      contenu de la formation, méthodes, évaluation.
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2479" data-mp="leob8ujlcknj">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2.5 shrink-0" data-id="element-2480" data-mp="lc24gx4iy96d"></div>
                    <span className="text-muted-foreground leading-relaxed" data-id="element-2481" data-mp="pd44vha6gr9x">
                      <strong className="text-primary-dark font-bold" data-id="element-2482" data-mp="ud8mxbqqhu29">
                        Administratif :
                      </strong>{' '}
                      inscription, contrat, facturation.
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2483" data-mp="192y1q9rr1wl0">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2.5 shrink-0" data-id="element-2484" data-mp="195ka2bmgsg4a"></div>
                    <span className="text-muted-foreground leading-relaxed" data-id="element-2485" data-mp="1d2yg5ueum7s2">
                      <strong className="text-primary-dark font-bold" data-id="element-2486" data-mp="1i2ui7o3zjp4z">
                        Organisationnel :
                      </strong>{' '}
                      planning, locaux, matériel.
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2487" data-mp="2olc5nz2dusli">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2.5 shrink-0" data-id="element-2488" data-mp="2ojrcacrt1fp8"></div>
                    <span className="text-muted-foreground leading-relaxed" data-id="element-2489" data-mp="2jlps1oin7ac6">
                      <strong className="text-primary-dark font-bold" data-id="element-2490" data-mp="2yiguk6nm5260">
                        Relation entreprise :
                      </strong>{' '}
                      suivi en entreprise, missions.
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2491" data-mp="25si4q91lh10a">
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-2.5 shrink-0" data-id="element-2492" data-mp="20x2t7nr27h71"></div>
                    <span className="text-muted-foreground leading-relaxed" data-id="element-2493" data-mp="2flfnmgai0rzf">
                      <strong className="text-primary-dark font-bold" data-id="element-2494" data-mp="2atnubxzyk85l">
                        Accessibilité :
                      </strong>{' '}
                      accueil PSH, aménagements.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="space-y-10" data-id="element-2495" data-mp="3trkg0f8m6le9">
              <Card className="bg-primary-dark text-white border-none shadow-xl hover:-translate-y-2 transition-transform duration-300 group" data-id="element-2496" data-mp="3tqivzl9meoke">
                <div className="h-2 w-full bg-primary-accent" data-id="element-2497" data-mp="3jv7cyqr6iv6u" />
                <CardContent className="p-8 md:p-10" data-id="element-2498" data-mp="3jsl8x13hyxtd">
                  <h3 className="text-2xl font-extrabold font-heading mb-6 flex items-center gap-4" data-id="element-2499" data-mp="3ouakqiyjnfii">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2500" data-mp="gf0k7faayicu">
                      <Mail className="w-6 h-6 text-lavande animate-soft-float" data-id="element-2501" data-mp="7d12ekrgra8u" />
                    </div>
                    Comment nous contacter ?
                  </h3>
                  <p className="text-lavande mb-6 leading-relaxed text-lg" data-id="element-2502" data-mp="bgpb56ru0bvo">
                    Toute réclamation doit être formulée par écrit et envoyée à
                    l'adresse email suivante :
                  </p>
                  <a href={`mailto:${CFA_INFO.email}`} className="text-xl font-bold text-brand-orange hover:text-white transition-colors underline underline-offset-4" data-id="element-2503" data-mp="12m4lwnswmjeg">
                    {CFA_INFO.email}
                  </a>
                  <p className="text-sm text-lavande/70 mt-6 leading-relaxed" data-id="element-2504" data-mp="stynav85d3qa">
                    Merci de préciser "Réclamation" dans l'objet de votre email
                    et de détailler au maximum le motif de votre insatisfaction.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg bg-white hover:-translate-y-1 transition-transform duration-300 group" data-id="element-2505" data-mp="9xgzuq0bzpzu">
                <CardContent className="p-8" data-id="element-2506" data-mp="4z5pme64jdh3">
                  <h3 className="font-extrabold font-heading text-xl text-primary-dark mb-6 flex items-center gap-4" data-id="element-2507" data-mp="50gs8anu2f6m">
                    <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2508" data-mp="1zcdmschko15w">
                      <Clock className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-2509" data-mp="27sme24a11d1t" />
                    </div>
                    Délais de traitement
                  </h3>
                  <div className="space-y-4 text-base" data-id="element-2510" data-mp="2gl7l4n4apvat">
                    <div className="flex justify-between items-center pb-4 border-b border-slate-100" data-id="element-2511" data-mp="2gnsmlb0om981">
                      <span className="text-muted-foreground font-medium" data-id="element-2512" data-mp="2lmn489mwxrqk">
                        Accusé de réception
                      </span>
                      <span className="font-bold text-primary-dark" data-id="element-2513" data-mp="1svda8b3jegt7">
                        Sous 48h ouvrées
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-2514" data-mp="22ub5rxzdralp">
                      <span className="text-muted-foreground font-medium" data-id="element-2515" data-mp="22vm4hjdyxs5y">
                        Réponse et plan d'action
                      </span>
                      <span className="font-bold text-primary-dark" data-id="element-2516" data-mp="3e8ooypj6v1ve">
                        Sous 10 jours ouvrés
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="text-center bg-muted p-10 md:p-12 rounded-3xl border-none shadow-sm" data-id="element-2517" data-mp="cxxofg90ier3">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-2518" data-mp="877km7g8ijyi">
              <CheckCircle2 className="w-10 h-10 text-primary animate-soft-bounce" data-id="element-2519" data-mp="mvlu3185p6o1" />
            </div>
            <p className="text-xl font-medium text-primary-dark leading-relaxed max-w-2xl mx-auto" data-id="element-2520" data-mp="hxk21qsr5x0f">
              Chaque réclamation est analysée par notre direction et fait
              l'objet d'une action corrective dans le cadre de notre démarche
              d'amélioration continue.
            </p>
          </div>
        </div>
      </div>
    </div>;
}